import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook for managing form sections
 * Tracks completion status and provides helper functions
 */
export const useFormSections = (initialValues) => {
    const [sectionStates, setSectionStates] = useState({});

    // Define all sections with their validation rules
    const sections = useMemo(() => [
        {
            id: 'examination',
            name: 'Physical Examination',
            icon: 'clipboard',
            required: true,
            fields: ['upshay', 'anupshay', 'jivha', 'nails', 'eyes', 'prakruti', 'nadi'],
        },
        {
            id: 'nada',
            name: 'Nada Examination',
            icon: 'music',
            required: false,
            fields: ['naad'],
        },
        {
            id: 'palpation',
            name: 'Palpation',
            icon: 'target',
            required: false,
            fields: ['sparshaSahatva'],
        },
        {
            id: 'laboratory',
            name: 'Laboratory Tests',
            icon: 'activity',
            required: false,
            fields: ['laboratory', 'laboratoryNote'],
        },
        {
            id: 'diagnosis',
            name: 'Diagnosis',
            icon: 'file-text',
            required: true,
            fields: ['rootCause', 'diagnosis'],
        },
        {
            id: 'treatment',
            name: 'Treatment Plan',
            icon: 'heart',
            required: true,
            fields: ['treatment', 'solution'],
        },
        {
            id: 'prescription',
            name: 'Prescription',
            icon: 'file-plus',
            required: true,
            fields: ['recomendedMedicines'],
        },
        {
            id: 'lifestyle',
            name: 'Lifestyle Advice',
            icon: 'sun',
            required: false,
            fields: ['dos', 'donts', 'virechan', 'exercise'],
        },
        {
            id: 'notes',
            name: 'Doctor Notes',
            icon: 'edit-3',
            required: false,
            fields: ['doctorNotes'],
        },
        {
            id: 'followup',
            name: 'Follow-up',
            icon: 'calendar',
            required: true,
            fields: ['nextConsultationDate'],
        },
    ], []);

    // Check if a field has value
    const hasValue = useCallback((value) => {
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value)) {
            if (value.length === 0) return false;
            // Check for prescription medicines
            if (value[0]?.medicineName !== undefined) {
                return value.some(med => med.medicineName && med.medicineName.trim() !== '');
            }
            // Check for diagnosis/treatment arrays
            if (value[0]?.diagnosis !== undefined || value[0]?.treatmentName !== undefined) {
                return value.some(item =>
                    (item.diagnosis && item.diagnosis.trim() !== '') ||
                    (item.treatmentName && item.treatmentName.trim() !== '')
                );
            }
            return value.some(v => v && v !== '');
        }
        return true;
    }, []);

    // Calculate completion status for a section
    const getSectionCompletion = useCallback((section, values) => {
        const filledFields = section.fields.filter(field => hasValue(values[field]));
        const totalFields = section.fields.length;
        const isCompleted = section.required
            ? filledFields.length === totalFields
            : filledFields.length > 0;

        return {
            isCompleted,
            filledFields: filledFields.length,
            totalFields,
            percentage: Math.round((filledFields.length / totalFields) * 100),
        };
    }, [hasValue]);

    // Get all sections with their completion status
    const getSectionsWithStatus = useCallback((values) => {
        return sections.map(section => ({
            ...section,
            ...getSectionCompletion(section, values),
        }));
    }, [sections, getSectionCompletion]);

    // Get overall form completion
    const getFormCompletion = useCallback((values) => {
        const sectionsWithStatus = getSectionsWithStatus(values);
        const completedRequired = sectionsWithStatus.filter(s => s.required && s.isCompleted).length;
        const totalRequired = sectionsWithStatus.filter(s => s.required).length;
        const allCompleted = sectionsWithStatus.filter(s => s.isCompleted).length;

        return {
            completedRequired,
            totalRequired,
            allCompleted,
            totalSections: sections.length,
            isFormValid: completedRequired === totalRequired,
            overallPercentage: Math.round((allCompleted / sections.length) * 100),
        };
    }, [sections, getSectionsWithStatus]);

    // Toggle section open/close
    const toggleSection = useCallback((sectionId) => {
        setSectionStates(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }));
    }, []);

    // Check if section is open
    const isSectionOpen = useCallback((sectionId) => {
        return sectionStates[sectionId] !== false; // Default to open
    }, [sectionStates]);

    return {
        sections,
        getSectionsWithStatus,
        getFormCompletion,
        toggleSection,
        isSectionOpen,
    };
};
